import { darkColor, textColor } from "@/constants/colors";
import { css } from "@emotion/react";
import Container from "../Container/Container";
import {
  Body,
  BodyRegular14Css,
  H3Css,
  H4Css,
  H5Css,
  H7Css,
  Heading,
} from "../Typography";
import Image from "next/image";
import Balancer from "react-wrap-balancer";
import { mq } from "@/constants/breakpoints";
import HR from "../HR/HR";
import { useWindowSize } from "@/hooks/useWindowSize";
import { contentfulLoader } from "../ContentfulImage/ContentfulImage";

const FeatureSectionSub = ({
  headline,
  body,
  isLast = false,
}: {
  headline: string;
  body: string;
  isLast?: boolean;
}) => {
  return (
    <div>
      <Heading
        level={3}
        css={css`
          ${H7Css}
          color: ${darkColor};
          ${mq["lg"]} {
            ${H4Css}
          }
        `}
      >
        <Balancer>{headline}</Balancer>
      </Heading>
      <Body
        css={css`
          margin-top: 12px;
          color: ${textColor};
          ${BodyRegular14Css}
          ${mq["lg"]} {
            margin-top: 18px;
          }
        `}
      >
        <Balancer>{body}</Balancer>
      </Body>
      {!isLast && (
        <div
          css={css`
            margin: 30px 0;
            width: 118px;
            ${mq["md"]} {
              width: 150px;
            }
            ${mq["lg"]} {
              margin: 40px 0;
            }
          `}
        >
          <HR />
        </div>
      )}
    </div>
  );
};

interface FeatureSectionProps {
  imageFirst?: boolean;
  imageSrc: string;
  sectionHeadline: string;
  sectionSEOText?: string;
  feat1Headline: string;
  feat1Body: string;
  feat2Headline: string;
  feat2Body: string;
  feat3Headline: string;
  feat3Body: string;
  feat4Headline?: string;
  feat4Body?: string;
}

const FeatureSection = ({
  imageSrc,
  imageFirst = false,
  sectionHeadline,
  sectionSEOText,
  feat1Headline,
  feat1Body,
  feat2Headline,
  feat2Body,
  feat3Headline,
  feat3Body,
  feat4Headline,
  feat4Body,
}: FeatureSectionProps) => {
  const { isXs } = useWindowSize();

  return (
    <Container
      css={css`
        margin-top: 60px;
        position: relative;
        background-color: #fff;
        ${mq["lg"]} {
          margin-top: 120px;
        }
      `}
    >
      {sectionSEOText && (
        <Heading
          level={2}
          css={css`
            font-size: 10px;
            font-weight: 500;
            line-height: 12px;
            text-align: center;
            text-transform: uppercase;
            color: #048cdd;
            ${mq["lg"]} {
              font-size: 24px;
              font-weight: 350;
              line-height: 31.21px;
            }
          `}
        >
          {sectionSEOText}
        </Heading>
      )}
      <div
        css={css`
          text-align: center;
          margin-top: ${sectionSEOText ? 24 : 0}px;
          ${mq["lg"]} {
            margin-top: ${sectionSEOText ? 30 : 0}px;
          }
        `}
      >
        <Heading
          level={2}
          css={css`
            ${H5Css}
            color: ${darkColor};
            ${mq["lg"]} {
              ${H3Css}
            }
          `}
        >
          <Balancer>{sectionHeadline}</Balancer>
        </Heading>
      </div>
      <div
        css={css`
          display: flex;
          margin-top: 24px;
          flex-direction: column;
          gap: 30px;
          ${mq["md"]} {
            margin-top: 50px;
            gap: 40px;
            flex-direction: row;
            flex-direction: ${imageFirst ? "row" : "row-reverse"};
          }
          ${mq["lg"]} {
            margin-top: 80px;
          }
        `}
      >
        <div
          css={css`
            flex: 1.15;
            ${mq["lg"]} {
              flex: 1.25;
            }
          `}
        >
          <Image
            src={imageSrc}
            loader={contentfulLoader}
            width={isXs ? 328 : 596}
            height={isXs ? 368 : 885}
            alt={sectionHeadline}
            css={css`
              width: 100%;
              max-width: 100%;
              height: auto;
              object-fit: contain;
              object-position: top;
              border-radius: 10px;
            `}
          />
        </div>
        <div
          css={css`
            flex: 1;
          `}
        >
          <FeatureSectionSub headline={feat1Headline} body={feat1Body} />
          <FeatureSectionSub headline={feat2Headline} body={feat2Body} />
          <FeatureSectionSub
            headline={feat3Headline}
            body={feat3Body}
            isLast={!feat4Headline}
          />
          {feat4Headline && feat4Body && (
            <FeatureSectionSub
              headline={feat4Headline}
              body={feat4Body}
              isLast
            />
          )}
        </div>
      </div>
    </Container>
  );
};

export default FeatureSection;
